import { PLACEHOLDER_IMAGE_USER } from '@presentation/constants';
import { useTranslation } from '@presentation/utils';
import dayjs from 'dayjs';
import { capitalize } from 'lodash/fp';
import type { FC } from 'react';
import { FaClock } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import * as Styled from './styled';
import type { MtBookingCardProps } from './types';

export * from './types';

export const MtBookingCard: FC<MtBookingCardProps> = (props) => {
  const { onClick, upcoming = false, booking, onDeleteClick, onUpdateClick } = props;

  const { translate: translateGeneral } = useTranslation();
  const { translate } = useTranslation({ keyPrefix: 'mt.buyer.booking' });

  return (
    <Styled.Wrapper onClick={onClick}>
      <Styled.Header active={upcoming}>
        {upcoming && (
          <Styled.TimecountWrapper>
            <FaClock />
            <Styled.Timecount>{capitalize(booking.startDate.fromNow())}</Styled.Timecount>
          </Styled.TimecountWrapper>
        )}
        <Styled.Title>
          {capitalize(booking.startDate.format('dddd D MMMM YYYY'))} •{' '}
          {booking.startDate.format('HH:mm')}
        </Styled.Title>
        <div>
          {booking.service} ({booking.duration})
        </div>
      </Styled.Header>

      <Styled.Section>
        <Styled.Image src={booking.pro.image ?? PLACEHOLDER_IMAGE_USER} />
        <Styled.SectionContent>
          {booking.pro.link ? (
            <Link to={booking.pro.link} target="_blank" rel="noreferrer">
              {booking.pro.name}
            </Link>
          ) : (
            <div>{booking.pro.name}</div>
          )}
          {!!booking.pro.email && <Styled.Text>{booking.pro.email}</Styled.Text>}
          {!!booking.pro.phone && <Styled.Text>{booking.pro.phone}</Styled.Text>}
        </Styled.SectionContent>
      </Styled.Section>

      {booking.animals.map((animal, index) => (
        <Styled.Section key={index}>
          <Styled.Image src={animal.image ?? PLACEHOLDER_IMAGE_USER} />
          <Styled.SectionContent>
            <Link to={animal.link} target="_blank" rel="noreferrer">
              {animal.name}
            </Link>
            <Styled.Text>
              {animal.gender && `${translateGeneral(`enum.gender.${animal.gender}`)} • `}
              {animal.birthday && dayjs().from(animal.birthday, true)}
            </Styled.Text>
          </Styled.SectionContent>
        </Styled.Section>
      ))}

      {!!upcoming && (
        <>
          {!!booking.warning && (
            <Styled.Section isGrey={!booking.canUpdate}>
              <Styled.WarningText>{booking.warning}</Styled.WarningText>
            </Styled.Section>
          )}

          {!!booking.canUpdate && (
            <Styled.ButtonContainer>
              <Styled.Button color="blue" onClick={onUpdateClick}>
                {translateGeneral('common.buttons.edit')}
              </Styled.Button>
              <Styled.Button color="red" onClick={onDeleteClick}>
                {translateGeneral('common.buttons.cancel')}
              </Styled.Button>
            </Styled.ButtonContainer>
          )}
        </>
      )}

      {!upcoming && (
        <Styled.ButtonContainer>
          <Link to={booking.pro.link}>{translateGeneral('common.buttons.newAppointment')}</Link>
        </Styled.ButtonContainer>
      )}
    </Styled.Wrapper>
  );
};
